import React from 'react'
import * as Icon from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { StyledSwitch } from '../../LocationsList/styles'
import Textarea from '~/components/Textarea'
import { useSelector } from 'react-redux'
import useAxios from 'axios-hooks'
import { toast } from 'react-toastify'

const TEMPLATE_MAX_LENGTH = 500

const TemplateCard = ({ template, removeById, updateById }) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [templateName, setTemplateName] = React.useState('')
  const [templateContent, setTemplateContent] = React.useState('')
  const [isTemporary, setIsTemporary] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const accountId = useSelector(state => state.user?.profile?.account?.id)

  const [, createTemplate] = useAxios(
    {
      url: `/accounts/${accountId}/templates/`,
      method: 'POST',
    },
    { manual: true }
  )

  const [, updateTemplate] = useAxios(
    {
      url: `/accounts/${accountId}/templates/${template.id}`,
      method: 'PATCH',
    },
    { manual: true }
  )

  const [, deleteTemplate] = useAxios(
    {
      url: `/accounts/${accountId}/templates/${template.id}`,
      method: 'DELETE',
    },
    { manual: true }
  )

  function handleOpen(event) {
    event.preventDefault()
    setIsOpen(prevState => !prevState)
  }

  function handleEdit(value) {
    setIsEditing(value)
  }

  async function handleDelete() {
    try {
      setIsLoading(true)
      if (!isTemporary) {
        await deleteTemplate()
        toast.success(
          intl.formatMessage({ id: 'Template deleted successfully!' })
        )
      }
      removeById(template.id)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'An error occurred!' }))
    }
    setIsLoading(false)
  }

  async function handleSave() {
    try {
      setIsLoading(true)
      if (isTemporary) {
        const { data: createdTemplate } = await createTemplate({
          data: {
            name: templateName,
            content: templateContent,
            category: template.category,
          },
        })
        updateById(template.id, createdTemplate)
        toast.success(
          intl.formatMessage({ id: 'Template created successfully!' })
        )
      } else {
        const { data: updatedTemplate } = await updateTemplate({
          data: {
            name: templateName,
            content: templateContent,
          },
        })
        updateById(template.id, updatedTemplate)
        toast.success(
          intl.formatMessage({ id: 'Template updated successfully!' })
        )
      }
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'An error occurred!' }))
    }
    setIsLoading(false)
  }

  React.useEffect(() => {
    if (template.id === 'temporary-id') {
      setIsOpen(true)
      setIsTemporary(true)
      setIsEditing(true)
    }

    setTemplateName(template?.name)
    setTemplateContent(template?.content)
  }, [template])

  React.useEffect(() => {
    if (!isEditing) {
      setTemplateName(template?.name)
      setTemplateContent(template?.content)
    }
  }, [isEditing, template])

  React.useEffect(() => {
    if (!isOpen && template.id !== 'temporary-id') {
      setIsEditing(false)
    }
  }, [isOpen, template])

  return (
    <StyledDetails open={isOpen}>
      <summary onClick={handleOpen}>
        <div>
          {template.category === 'reviews' ? (
            <Icon.Star color="#2f2b3de5" />
          ) : (
            <Icon.HelpCircle color="#2f2b3de5" />
          )}
          {isTemporary ? <FormattedMessage id="New Template" /> : template.name}
        </div>
        {isOpen ? (
          <Icon.ChevronUp color="#2f2b3de5" />
        ) : (
          <Icon.ChevronDown color="#2f2b3de5" />
        )}
      </summary>
      <div className="template-form">
        <CustomSwitch
          id={`edit-switch-${template.id}`}
          checked={isEditing}
          style={{ zIndex: '0 !important' }}
          onChange={e => handleEdit(e.target.checked)}
          type="switch"
          disabled={isTemporary || isLoading}
          inline
        >
          <FormattedMessage id="Edit" />
        </CustomSwitch>
        <div>
          <label>
            <FormattedMessage id="Template Name:" />
          </label>
          <input
            className="form-control"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            disabled={!isEditing || isLoading}
          />
        </div>
        <div>
          <label>
            <FormattedMessage id="Content:" />
          </label>
          <Textarea
            className="form-control"
            value={templateContent}
            onChange={e => setTemplateContent(e.target.value)}
            setValue={v => setTemplateContent(v)}
            maxLength={TEMPLATE_MAX_LENGTH}
            disabled={!isEditing || isLoading}
            enableVariables
          />
        </div>
        <div className="template-buttons">
          <button onClick={handleDelete} disabled={!isEditing || isLoading}>
            <FormattedMessage id={isTemporary ? 'Cancel' : 'Delete'} />
          </button>
          <button
            onClick={handleSave}
            disabled={
              !isEditing ||
              isLoading ||
              !templateContent ||
              !templateName ||
              templateContent.length > TEMPLATE_MAX_LENGTH
            }
          >
            <FormattedMessage id="Save Changes" />
          </button>
        </div>
      </div>
    </StyledDetails>
  )
}

const StyledDetails = styled.details`
  border-radius: 0.5rem;
  border: 1px solid #00000026;
  background: #fff;
  padding: 1.5rem;

  summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    list-style: none;

    div {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #2f2b3de5;
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
    }

    &::-webkit-details-marker {
      display: none;
    }
  }

  .template-form {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .template-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;

    button:first-child {
      color: #ed5f5f;
      background-color: transparent;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
    }
  }
`

const CustomSwitch = styled(StyledSwitch)`
  display: flex;
  flex-direction: row;

  span {
    margin-right: 10px;
  }
`

export default TemplateCard
